import ObjectActions from './ObjectActions.jsx';
import { cachedJSON } from '../utils/cachedJSON.js';
import CloseIcon from './CloseIcon.jsx';
import ImageAttribution from './ImageAttribution.jsx';
import {passWindow} from '../utils/observations.js';
import { useState, useEffect } from 'react';
import { useDatasheet } from '../hooks/useDatasheet.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import AISummary from './AISummary.jsx';
import SatelliteVideo from './SatelliteVideo.jsx';
import {LIVE_STREAMS} from '../utils/satelliteVideos.js';
import ISSDetailPanel from './ISSDetailPanel.jsx';
import { tierFor } from '../hooks/useTierC.js';
import { tierColor as passTierColor } from '../utils/passTiers.js';

const API = import.meta.env.VITE_API_BASE || '/api';

// Tier display helpers, TIER_COLORS imported from passTiers (shared)
const TIER_ICONS  = ['', '👁', '⭐', '📷'];
const TIER_LABELS = ['Not visible', 'Naked-eye visible', 'Worth going outside', 'Photograph this'];
function fmtTime(iso) { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
function fmtCountdown(iso) {
  const diff = new Date(iso) - Date.now();
  if (diff < 0) return 'Now';
  const m = Math.floor(diff / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  if (m > 60) return `in ${Math.floor(m / 60)}h ${m % 60}m`;
  return m > 0 ? `in ${m}m ${sec}s` : `in ${sec}s`;
}

// Expanded datasheet card, the "tell me everything" view of a single object.
// Consumed from the unifying GET /api/satellites/:norad/datasheet endpoint
// shipped in Phase 5. Pairs with the compact ObjectPopup which anchors to
// the object on the globe; this card is screen-fixed (right side on
// desktop, full-screen on mobile) so the user can scroll and read without
// the popup chasing the satellite around.
//
// Field shape from the API:
//   { value, source, disagreements?: [{ source, value }] }
// The card renders the canonical value prominently, the source as a
// small colored tag, and disagreements (if any) as an expandable detail
// that the user can pop open to see "Wikidata says X, GCAT says Y" -
// the honest-display pattern that Phase 5 was built around.

const SOURCE_COLORS = {
  curation: { bg: '#ffe066', fg: '#1a1a00' },  // gold, Phase 7 hand-curated
  wikidata: { bg: '#a78bfa', fg: '#1a0033' },  // purple, Wikidata/Wikimedia
  satcat:   { bg: '#5a8aff', fg: '#001033' },  // blue, Space-Track SATCAT
  tle:      { bg: 'var(--cyan)', fg: '#001033' },  // pale blue, current TLE
  ucs:      { bg: '#69ff94', fg: '#003322' },  // green, UCS database
  gcat:     { bg: '#ff9e64', fg: '#331a00' },  // orange, Jonathan McDowell's GCAT
};

function SourceTag({ source }) {
  if (!source) return null;
  const c = SOURCE_COLORS[source] || { bg: '#888', fg: '#fff' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 6px',
      borderRadius: 3,
      fontSize: 'var(--ui-small)',
      fontFamily: 'var(--mono)',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.fg,
      fontWeight: 600,
      verticalAlign: 'middle',
      marginLeft: 8,
    }}>{source}</span>
  );
}

// Renders a single field row. Accepts either:
//   - a field object { value, source, disagreements? }
//   - null/undefined (renders nothing, caller decides to skip)
//   - a plain value (renders without source tag)
function FieldRow({ label, field, format }) {
  // useState must be at the top, Rules of Hooks. Even when field is null
  // or a plain value, we still want the hook to be called in the same
  // order across renders since the same FieldRow position may receive
  // different shapes as data loads.
  const [open, setOpen] = useState(false);
  if (field == null) return null;
  // Plain values (numbers, strings), render without provenance.
  if (typeof field !== 'object' || !('value' in field)) {
    const formatted = format ? format(field) : String(field);
    return (
      <div style={ROW_STYLE}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{formatted}</div>
      </div>
    );
  }
  const formatted = format ? format(field.value) : String(field.value);
  const hasDisagreements = field.disagreements && field.disagreements.length > 0;
  return (
    <div style={ROW_STYLE}>
      <div style={LABEL_STYLE}>{label}</div>
      <div style={VALUE_STYLE}>
        <span>{formatted}</span>
        <SourceTag source={field.source} />
        {hasDisagreements && (
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            title={`${field.disagreements.length} other source${field.disagreements.length === 1 ? '' : 's'} disagree`}
            style={DISAGREE_TOGGLE_STYLE}
          >
            {open ? '−' : '+'}{field.disagreements.length}
          </button>
        )}
        {hasDisagreements && open && (
          <div style={DISAGREE_PANEL_STYLE}>
            {field.disagreements.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                <SourceTag source={d.source} />
                <span style={{ color: 'var(--cyan)' }}>{format ? format(d.value) : String(d.value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// ─── Styles ──────────────────────────────────────────────────────────────────
const ROW_STYLE = {
  display: 'grid',
  gridTemplateColumns: '128px 1fr',
  alignItems: 'baseline',
  gap: 12,
  padding: '6px 0',
  borderBottom: '1px solid var(--hair)',
  fontSize: 'var(--ui-body)',
};
const LABEL_STYLE = {
  color: 'var(--txt-dim)',
  fontFamily: 'var(--mono)',
  fontSize: 'var(--ui-label)',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
};
const VALUE_STYLE = {
  color: 'var(--txt-bright)',
  wordBreak: 'break-word',
  lineHeight: 1.4,
};
const DISAGREE_TOGGLE_STYLE = {
  marginLeft: 8,
  padding: '0 6px',
  borderRadius: 3,
  border: '1px solid rgba(233,179,93,0.4)',
  background: 'var(--amber-2)',
  color: 'var(--accent)',
  fontFamily: 'var(--mono)',
  fontSize: 'var(--ui-label)',
  cursor: 'pointer',
  verticalAlign: 'middle',
};
const DISAGREE_PANEL_STYLE = {
  marginTop: 6,
  paddingLeft: 10,
  borderLeft: '2px solid rgba(233,179,93,0.4)',
  fontSize: 'var(--ui-label)',
};
const SECTION_HEADER_STYLE = {
  fontFamily: 'var(--mono)',
  fontSize: 'var(--ui-label)',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--cyan)',
  marginTop: 18,
  marginBottom: 4,
  paddingBottom: 4,
  borderBottom: '1px solid var(--hair-2)',
};

function Section({ title, children }) {
  // Skip rendering entirely if all children are null/false
  const kids = Array.isArray(children) ? children.filter(Boolean) : children;
  const hasContent = Array.isArray(kids) ? kids.length > 0 : !!kids;
  if (!hasContent) return null;
  return (
    <details className="og-record-section">
      <summary>{title}</summary>
      {children}
    </details>
  );
}

// ─── Value formatters ────────────────────────────────────────────────────────
const fmtDate = v => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toISOString().slice(0, 10);
};
const fmtKg = v => {
  if (v == null) return '-';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return String(v);
  if (n >= 1000) return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;
};
const fmtMeters = v => {
  if (v == null) return '-';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return String(v);
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} m`;
};
const fmtKm = v => {
  if (v == null) return '-';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return String(v);
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} km`;
};
const fmtDeg = v => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return '-';
  return `${n.toFixed(2)}°`;
};
const fmtMin = v => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return '-';
  return `${n.toFixed(1)} min`;
};
const fmtYears = v => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n)) return '-';
  return `${n.toFixed(1)} yr`;
};

// ─── Main component ──────────────────────────────────────────────────────────
// Props:
//   norad      , NORAD id to fetch/display
//   onCollapse , called when user closes/collapses the card
//   panelMode  , when true, fills its container (used in RightConsole).
//                 when false/absent, uses fixed positioning (expand button).
//   sat        , live satellite position object from the propagator (optional).
//                 When present enables live telemetry strip, AISummary, and
//                 pass prediction sections.
//   tracked    , boolean, whether this NORAD is in camera-follow mode
//   observer   , observer location object { lat, lon, name }
//   onFollow   , called to engage camera follow; receives sat object
//   onUntrack  , called to disengage follow

export default function DatasheetCard({ norad, onCollapse, panelMode, sat, tracked, observer, onFollow, onUntrack, hideHeader = false, hideActions = false }) {
  const isMobile = useIsMobile();
  const { data, loading, error } = useDatasheet(norad);

  // Pass prediction state, only populated when observer + real orbital sat
  const [nextPass,    setNextPass]    = useState(null);
  const [passLoading, setPassLoading] = useState(false);
  useEffect(() => {
    const skip = ['aviation', 'groundstation', 'launches'];
    if (!observer || !sat?.norad_id || skip.includes(sat?.category)) {
      setNextPass(null); return;
    }
    let cancelled = false;
    setPassLoading(true);
    cachedJSON(`${API}/satellites/${sat.norad_id}/passes?lat=${observer.lat}&lon=${observer.lon}&days=3`)
      .then(d => { if (!cancelled) { setNextPass(d.passes?.[0] || null); setPassLoading(false); } })
      .catch(() => { if (!cancelled) setPassLoading(false); });
    return () => { cancelled = true; };
  }, [sat?.norad_id, observer?.lat, observer?.lon]);

  // Share URL helper
  function handleShare() {
    const url = `${window.location.origin}${window.location.pathname}?norad=${norad}`;
    if (navigator.share) { navigator.share({ title: `OrbitalGlass, NORAD ${norad}`, url }).catch(() => {}); }
    else { navigator.clipboard.writeText(url).catch(() => {}); }
  }

  // Container: panel mode fills the parent (RightConsole); otherwise fixed overlay.
  const PANEL_CONTAINER = {
    width: '100%', height: '100%', minHeight:0,
    background: 'rgba(10,10,12,0.97)',
    display: 'flex', flexDirection: 'column',
    fontFamily: 'var(--sans)',
    overflow: 'hidden',
  };
  const containerStyle = panelMode ? PANEL_CONTAINER : isMobile ? MOBILE_CONTAINER : DESKTOP_CONTAINER;

  if (loading) {
    return (
      <div style={containerStyle}>
        {!hideHeader && <Header norad={norad} title="Loading datasheet…" onCollapse={onCollapse} />}
        <div style={{ ...BODY_STYLE, color: 'var(--txt-dim)' }}>Fetching unified record…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={containerStyle}>
        {!hideHeader && <Header norad={norad} title={`NORAD ${norad}`} onCollapse={onCollapse} />}
        <div style={{ ...BODY_STYLE, color: 'var(--rose)' }}>
          {error.includes('not found')
            ? `No enrichment data available for NORAD ${norad}.`
            : `Error: ${error}`}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const f = data.fields;
  const heroImage = data.images?.find(i => i.role === 'hero') || data.images?.[0];

  return (
    <div style={containerStyle}>
      {!hideHeader && <Header
        norad={norad}
        title={f.identity?.name?.value || `NORAD ${norad}`}
        onCollapse={onCollapse}
        tracked={tracked}
        sat={sat}
        onFollow={onFollow}
        onUntrack={onUntrack}
        onShare={Number.isFinite(norad) ? handleShare : null}
      />}
      {!hideActions && sat && onFollow && <ObjectActions sat={sat} tracked={tracked} onFollow={onFollow} onUntrack={onUntrack}/>}

      <div data-testid="satellite-details-scroll" role="region" aria-label="Satellite details" tabIndex={0} style={BODY_STYLE}>
        <SatelliteVideo key={norad} streams={LIVE_STREAMS[norad]} />
      {heroImage && (
        <div style={{ position: 'relative', borderBottom: '1px solid var(--hair-2)' }}>
          <img
            src={heroImage.url}
            alt={f.identity?.name?.value || `NORAD ${norad}`}
            style={{ display: 'block', width: '100%', maxHeight: 120, objectFit: 'cover' }}
          />
          {/* Representative image badge, shown for group/constellation images */}
          {heroImage.is_group_image && (
            <div style={{
              position: 'absolute', top: 8, left: 8,
              padding: '2px 8px', borderRadius: 3,
              background: 'rgba(10,10,12,0.82)',
              color: 'var(--txt-dim)', fontSize: 'var(--ui-small)',
              fontFamily: 'var(--mono)',
              letterSpacing: '0.10em', textTransform: 'uppercase',
              border: '1px solid var(--hair-2)',
            }}>
              Representative · {heroImage.group_label}
            </div>
          )}
          {(heroImage.attribution_text || heroImage.attribution_url || heroImage.license) && (
            <div style={IMAGE_ATTRIBUTION_STYLE}>
              <ImageAttribution text={heroImage.attribution_text} url={heroImage.attribution_url} license={heroImage.license} />
            </div>
          )}
        </div>
      )}

        {/* Live telemetry strip, only when sat position is available */}
        {sat && !['aviation','groundstation','launches'].includes(sat.category) && (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '2px 8px', padding: '8px 0 10px',
            borderBottom: '1px solid var(--hair)',
            marginBottom: 8, fontSize: 'var(--ui-label)',
            fontFamily: 'var(--mono)',
          }}>
            {sat.alt_km != null && (
              <div><span style={{ color: 'var(--txt-dim)' }}>ALT </span>
                <span style={{ color: 'var(--txt)' }}>{sat.alt_km?.toFixed(0)} km</span></div>
            )}
            {sat.velocity_kms != null && (
              <div><span style={{ color: 'var(--txt-dim)' }}>VEL </span>
                <span style={{ color: 'var(--txt)' }}>{sat.velocity_kms} km/s</span></div>
            )}
            {sat.period_min != null && (
              <div><span style={{ color: 'var(--txt-dim)' }}>PER </span>
                <span style={{ color: 'var(--txt)' }}>{sat.period_min} min</span></div>
            )}
            {sat.inclination != null && (
              <div><span style={{ color: 'var(--txt-dim)' }}>INC </span>
                <span style={{ color: 'var(--txt)' }}>{sat.inclination?.toFixed(1)}°</span></div>
            )}
            {sat.sun_illuminated != null && (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <span style={{
                  display: 'inline-block', padding: '1px 8px', borderRadius: 99,
                  fontSize: 'var(--ui-label)', fontWeight: 700,
                  background: sat.sun_illuminated ? 'var(--amber-2)' : 'var(--bg-2)',
                  color: sat.sun_illuminated ? 'var(--accent)' : 'var(--txt-dim)',
                  border: `1px solid ${sat.sun_illuminated ? 'rgba(233,179,93,0.28)' : 'var(--hair)'}`,
                }}>
                  {sat.sun_illuminated ? '☀ Sunlit' : '● Shadow'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* AI summary, orbital objects with position data */}
        {sat && !['aviation','groundstation','launches'].includes(sat.category) && (
          <AISummary sat={sat} detail={null} />
        )}

        {/* Source availability strip, at-a-glance "which catalogs have this object?" */}
        <details className="og-record-section"><summary>Catalog coverage</summary><SourceAvailabilityStrip sources={data.sources_available} /></details>

        <Section title="Identity">
          <FieldRow label="Name"        field={f.identity?.name} />
          <FieldRow label="Description" field={f.identity?.description} />
          <FieldRow label="NORAD ID"    field={norad} />
          <FieldRow label="COSPAR"      field={f.identity?.cospar_id} />
          <FieldRow label="JCAT"        field={f.identity?.jcat} />
          <FieldRow label="Wikidata"    field={f.identity?.wikidata_qid} format={v => (
            <a href={`https://www.wikidata.org/wiki/${v}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>{v}</a>
          )} />
          <FieldRow label="Wikipedia"   field={f.identity?.wikipedia_url} format={v => (
            <a href={v} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>{v.replace(/^https?:\/\//, '').replace(/^en\.wikipedia\.org\/wiki\//, '')}</a>
          )} />
        </Section>

        <Section title="Classification">
          <FieldRow label="Types"    field={f.classification?.types} />
          <FieldRow label="Bus"      field={f.classification?.bus} />
          <FieldRow label="Motor"    field={f.classification?.motor} />
          <FieldRow label="Shape"    field={f.classification?.shape} />
          <FieldRow label="Country"  field={f.classification?.country} />
          <FieldRow label="Category" field={f.classification?.category} />
        </Section>

        <Section title="Launch">
          <FieldRow label="Date"      field={f.launch?.date} format={fmtDate} />
          <FieldRow label="Site"      field={f.launch?.site} />
          <FieldRow label="Site Name" field={f.launch?.site_name} />
          <FieldRow label="Vehicle"   field={f.launch?.vehicle} />
        </Section>

        <Section title="Mass & Dimensions">
          <FieldRow label="Mass"        field={f.mass_and_dimensions?.mass_kg}       format={fmtKg} />
          <FieldRow label="Dry Mass"    field={f.mass_and_dimensions?.dry_mass_kg}   format={fmtKg} />
          <FieldRow label="Total Mass"  field={f.mass_and_dimensions?.total_mass_kg} format={fmtKg} />
          <FieldRow label="Length"      field={f.mass_and_dimensions?.length_m}      format={fmtMeters} />
          <FieldRow label="Diameter"    field={f.mass_and_dimensions?.diameter_m}    format={fmtMeters} />
          <FieldRow label="Span"        field={f.mass_and_dimensions?.span_m}        format={fmtMeters} />
          <FieldRow label="Radar RCS"   field={f.mass_and_dimensions?.radar_rcs} />
          <FieldRow label="RCS Size"    field={f.mass_and_dimensions?.radar_rcs_size} />
        </Section>

        <Section title="Operations">
          <FieldRow label="Operator"     field={f.operations?.operator} />
          <FieldRow label="Manufacturer" field={f.operations?.manufacturer} />
          <FieldRow label="Purpose"      field={f.operations?.purpose} />
          <FieldRow label="Users"        field={f.operations?.users} />
          <FieldRow label="Design Life"  field={f.operations?.expected_life_yrs} format={fmtYears} />
        </Section>

        <Section title="Orbit">
          <FieldRow label="Inclination" field={f.orbit?.inclination_deg} format={fmtDeg} />
          <FieldRow label="Period"      field={f.orbit?.period_min}      format={fmtMin} />
          <FieldRow label="Apogee"      field={f.orbit?.apogee_km}       format={fmtKm} />
          <FieldRow label="Perigee"     field={f.orbit?.perigee_km}      format={fmtKm} />
          <FieldRow label="Orbit Class" field={f.orbit?.orbit_class} />
        </Section>

        <Section title="Status">
          <FieldRow label="Status"     field={f.status?.status} />
          <FieldRow label="Decay Date" field={f.status?.decay_date} format={fmtDate} />
        </Section>

        {/* ISS-specific crew manifest, only for NORAD 25544 */}
        {norad === 25544 && <ISSDetailPanel />}
        {/* Reentry alert, rendered whenever sat.reentry is populated, regardless
            of how the object was selected (globe click, search, event feed, etc.) */}
        {sat && sat.reentry && (() => {
          const r = sat.reentry;
          const daysLeft = (new Date(r.decay_epoch) - Date.now()) / 86400000;
          const urgency = daysLeft < 7 ? 'var(--red)' : daysLeft < 14 ? 'var(--warn)' : 'var(--accent)';
          const corridorTier = tierFor(r.decay_epoch, r.uncertainty_days);
          return (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--ui-label)',
                            letterSpacing: '0.18em', textTransform: 'uppercase',
                            color: 'var(--red)', marginTop: 18, marginBottom: 4,
                            paddingBottom: 4, borderBottom: '1px solid rgba(224,90,90,0.35)' }}>
                &#9888; Predicted Reentry
              </div>
              <div style={{ fontSize: 'var(--ui-body)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '128px 1fr', gap: 12,
                              padding: '5px 0', borderBottom: '1px solid rgba(224,90,90,0.12)' }}>
                  <div style={LABEL_STYLE}>Date</div>
                  <div style={{ color: urgency, fontWeight: 700 }}>
                    {new Date(r.decay_epoch).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' })}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '128px 1fr', gap: 12,
                              padding: '5px 0', borderBottom: '1px solid rgba(224,90,90,0.12)' }}>
                  <div style={LABEL_STYLE}>Time Until</div>
                  <div style={{ color: urgency, fontWeight: 700 }}>
                    {daysLeft < 1 ? `${Math.floor(daysLeft * 24)}h remaining` : `${daysLeft.toFixed(1)} days`}
                  </div>
                </div>
                {r.uncertainty_days > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '128px 1fr', gap: 12,
                                padding: '5px 0', borderBottom: '1px solid rgba(224,90,90,0.12)' }}>
                    <div style={LABEL_STYLE}>Uncertainty</div>
                    <div style={{ color: 'var(--txt-bright)' }}>{'+/-'}{r.uncertainty_days.toFixed(1)} days</div>
                  </div>
                )}
                {r.source && (
                  <div style={{ display: 'grid', gridTemplateColumns: '128px 1fr', gap: 12,
                                padding: '5px 0', borderBottom: '1px solid rgba(224,90,90,0.12)' }}>
                    <div style={LABEL_STYLE}>Source</div>
                    <div style={{ color: r.source === 'tip' ? 'var(--rose)' : 'var(--txt-dim)',
                                  fontWeight: r.source === 'tip' ? 700 : 400 }}>
                      {r.source === 'tip' ? 'Space-Track TIP (refined)' : 'Space-Track decay msg'}
                    </div>
                  </div>
                )}
                {corridorTier === 'far' && (
                  <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', marginTop: 6, lineHeight: 1.6, fontStyle: 'italic' }}>
                    Ground-track corridor unavailable, decay timing too uncertain for accurate projection.
                  </div>
                )}
                <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', marginTop: 4, lineHeight: 1.6 }}>
                  Exact reentry location cannot be predicted. Object will largely disintegrate on reentry.
                </div>
              </div>
            </div>
          );
        })()}

        {data.references?.length > 0 && (
          <div>
            <div style={SECTION_HEADER_STYLE}>References</div>
            <div style={REFERENCES_GRID_STYLE}>
              {data.references.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" style={REFERENCE_LINK_STYLE} title={r.note || r.label}>
                  <span style={{ color: r.kind === 'direct' ? 'var(--cyan)' : 'var(--txt-dim)' }}>{r.label}</span>
                  {r.kind === 'search' && <span style={{ fontSize: 'var(--ui-small)', color: 'var(--cyan)', marginLeft: 4 }}>(search)</span>}
                </a>
              ))}
            </div>
          </div>
        )}

        <div style={FOOTER_STYLE}>
          Generated {new Date(data.generated_at).toISOString().replace('T', ' ').slice(0, 19)} UTC
        </div>

        {/* Pass prediction, shown when observer is set and sat is a real orbital object */}
        {observer && sat && !['aviation','groundstation','launches'].includes(sat.category) && (
          <div style={{ marginTop: 14 }}>
            <div style={SECTION_HEADER_STYLE}>Next Pass · {observer.name?.split(',')[0] || 'observer'}</div>
            {passLoading && (
              <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', padding: '6px 0' }}>Computing…</div>
            )}
            {!passLoading && !nextPass && (
              <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', padding: '6px 0' }}>No passes in next 3 days</div>
            )}
            {!passLoading && nextPass && (() => {
              const tier = nextPass.visibility_tier ?? null;
              const tc   = tier !== null ? passTierColor(tier) : null;
              return (
                <>
                  {tier > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '6px 10px', borderRadius: 6, marginBottom: 6,
                                  background: tc + '15', border: `1px solid ${tc}33` }}>
                      <span style={{ fontSize: 'var(--ui-body)' }}>{TIER_ICONS[tier]}</span>
                      <span style={{ fontSize: 'var(--ui-body)', fontWeight: 700, color: tc }}>{TIER_LABELS[tier]}</span>
                    </div>
                  )}
                  <div style={{ fontSize: 'var(--ui-body)', color: 'var(--txt)', lineHeight: 1.9, fontFamily: 'var(--mono)' }}>
                    <div>
                      <span style={{ color: 'var(--txt)', fontWeight: 600 }}>{fmtTime(nextPass.start)}</span>
                      {' → '}
                      <span style={{ color: 'var(--txt)', fontWeight: 600 }}>{nextPass.end ? fmtTime(nextPass.end) : '?'}</span>
                      <span style={{ color: 'var(--txt-dim)', marginLeft: 8 }}>{fmtCountdown(nextPass.start)}</span>
                    </div>
                    <div className="og-pass-window">{passWindow(nextPass)}</div><div>Max <strong style={{ color: 'var(--txt-bright)' }}>{nextPass.max_el?.toFixed(0)}°</strong>
                      {' · '}Duration <strong style={{ color: 'var(--txt-bright)' }}>{nextPass.duration_sec}s</strong>
                    </div>
                    {nextPass.approx_magnitude != null && (
                      <div>Mag <strong style={{ color: 'var(--txt-bright)' }}>
                        {nextPass.approx_magnitude > 0 ? '+' : ''}{nextPass.approx_magnitude}
                      </strong></div>
                    )}
                    <div><strong style={{ color: 'var(--txt-bright)' }}>{nextPass.darkness}</strong></div>
                  </div>
                </>
              );
            })()}
          </div>
        )}


      </div>
    </div>
  );
}

// ─── Header & layout helpers ─────────────────────────────────────────────────
function Header({ norad, title, onCollapse, tracked, sat, onFollow, onUntrack, onShare }) {
  return (
    <div style={HEADER_STYLE}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--ui-label)', color: 'var(--cyan)', letterSpacing: '0.18em' }}>
          NORAD {norad}
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--txt-bright)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={title}>
          {title}
        </div>
      </div>
      <button aria-label="Close object details" className="og-close-control" type="button" onClick={onCollapse} title="Close" style={CLOSE_BUTTON_STYLE}><CloseIcon /></button>
    </div>
  );
}

function SourceAvailabilityStrip({ sources }) {
  if (!sources) return null;
  // Show one chip per source, bright if available, dimmed if not.
  const entries = [
    { key: 'curation', label: 'CURATED',  tip: 'Hand-curated metadata from the OrbitalGlass team: descriptions, hero imagery, mission tags, observer-facing context.' },
    { key: 'tle',      label: 'TLE',      tip: 'Two-Line Element set from US Space Force / Space-Track. Source of orbital position, velocity, and pass predictions. Refreshed multiple times daily.' },
    { key: 'satcat',   label: 'SATCAT',   tip: 'NORAD Satellite Catalog (Space-Track). Authoritative source of NORAD ID, COSPAR / international designator, launch date, country, and decay status.' },
    { key: 'ucs',      label: 'UCS',      tip: 'Union of Concerned Scientists Satellite Database. Adds purpose, operator, launch vehicle, mass, expected lifetime, and orbital class for active satellites.' },
    { key: 'gcat',     label: 'GCAT',     tip: "Jonathan McDowell's General Catalog of Artificial Space Objects (planet4589.org). Every object launched since 1957 with object type, orbit class, status, and detailed launch history." },
    { key: 'wikidata', label: 'WIKIDATA', tip: 'Wikidata (Wikimedia). Public knowledge graph: spacecraft name in multiple languages, manufacturer, instruments aboard, mission timeline, Wikipedia links.' },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid var(--hair-2)' }}>
      {entries.map(e => {
        const on = !!sources[e.key];
        const c = SOURCE_COLORS[e.key] || { bg: '#888', fg: '#fff' };
        return (
          <span key={e.key} title={on ? e.tip : (e.tip + ' (no data available for this object)')} style={{
            padding: '2px 8px',
            borderRadius: 3,
            fontSize: 'var(--ui-small)',
            fontFamily: 'var(--mono)',
            letterSpacing: '0.12em',
            background: on ? c.bg : 'var(--bg-2)',
            color: on ? c.fg : 'rgba(160,180,200,0.4)',
            border: `0.5px solid ${on ? c.bg : 'var(--hair-2)'}`,
            cursor: 'help',
          }}>{e.label}</span>
        );
      })}
    </div>
  );
}

// ─── Container styles ────────────────────────────────────────────────────────
const DESKTOP_CONTAINER = {
  position: 'fixed',
  top: 88,
  right: 16,
  bottom: 16,
  width: 420,
  background: 'rgba(10,10,12,0.94)',
  backdropFilter: 'blur(10px)',
  border: '1px solid var(--hair-2)',
  borderRadius: 6,
  display: 'flex',
  flexDirection: 'column',
  zIndex: 80,
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  fontFamily: 'var(--sans)',
};
const MOBILE_CONTAINER = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(10,10,12,0.98)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 80,
  fontFamily: 'var(--sans)',
};
const HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderBottom: '1px solid var(--hair-2)',
  flexShrink: 0,
};
const CLOSE_BUTTON_STYLE = {
  width: 32, height: 32,
  border: '0.5px solid var(--hair-2)',
  borderRadius: 4,
  background: 'var(--bg-2)',
  color: 'var(--txt-bright)',
  fontSize: 18, lineHeight: 1,
  cursor: 'pointer',
  flexShrink: 0,
};
const BODY_STYLE = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '0 14px 14px',
};
const IMAGE_ATTRIBUTION_STYLE = {
  padding: '6px 10px',
  lineHeight: 1.5,
  background: 'rgba(0,0,0,0.65)',
  color: 'var(--cyan)',
  fontSize: 'var(--ui-small)',
  fontFamily: 'var(--mono)',
  letterSpacing: '0.06em',
};
const REFERENCES_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 4,
  marginTop: 6,
};
const REFERENCE_LINK_STYLE = {
  padding: '6px 8px',
  borderRadius: 3,
  border: '0.5px solid var(--hair-2)',
  background: 'var(--bg-2)',
  textDecoration: 'none',
  fontSize: 'var(--ui-label)',
  display: 'flex',
  alignItems: 'baseline',
};
const FOOTER_STYLE = {
  marginTop: 18,
  paddingTop: 8,
  borderTop: '1px solid var(--hair-2)',
  fontSize: 'var(--ui-small)',
  color: 'var(--txt-dim)',
  fontFamily: 'var(--mono)',
  letterSpacing: '0.08em',
};
